import { placeInMapsLink, spotLink, type Spot } from '../functions/lib/maps-link'
import { lineDrawing, type Drawing } from './picture-tiles'
import { clear, el } from './ui'

// Pin the exact spot (Liam, 27 Oct 2026: "There is no way to take a google link or in the adress ie job i didnt today i
// need to pin as was so knew that google maps often take to wrong address"; picked with design B,
// https://claude.ai/artifact/JazPk5KEq5dttDN9iwETN4, Astra's check). Two ways in: "I'm here now" takes where the phone
// is, best at the door; "Paste a link" takes a Google Maps link (a shared short link is followed by /api/maps-place).
// Either is checked before it is saved: how close the phone could tell, and Google Maps to look at it. Saved as the
// customer's Map link (functions/lib/maps-link.ts), with Undo; every Directions then goes there.

// The phone's own estimate of how far out it may be (the Geolocation API's accuracy, a radius). Rougher than this is no
// use for finding a door, so it is never saved from "I'm here now" (Astra: a threshold of our own choosing).
export const ROUGHEST_METRES = 30

export interface SpotCustomer { id: number; name: string; link: string | null }

const first = (name: string): string => name.trim().split(/\s+/)[0] || name

// The line under an address: "Pin the exact spot", or "Exact spot pinned" once there is one. Either opens the sheet.
// `after` hears the link now saved (null when the pin was taken off); an Undo calls it with nothing.
export function spotButton(c: SpotCustomer, after: (link?: string | null) => void, beforeOpen?: () => void): HTMLButtonElement {
  const pinned = !!placeInMapsLink(c.link)
  const b = el('button', { type: 'button', class: `spot-line${pinned ? ' is-pinned' : ''}`, 'aria-label': pinned ? `Exact spot pinned for ${c.name}: change it` : `Pin the exact spot for ${c.name}` },
    lineDrawing('pin', 16), pinned ? 'Exact spot pinned' : 'Pin the exact spot') as HTMLButtonElement
  b.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); beforeOpen?.(); openSpotSheet(c, after) })
  return b
}

type Found = { spot: Spot; metres: number | null }

// `choose`: someone not saved yet (New customer) is given the link instead of it being saved. `start`: straight into
// "I'm here now" or "Paste a link" when the tile was tapped before the sheet opened.
export function openSpotSheet(c: SpotCustomer, after: (link?: string | null) => void, choose?: (link: string | null) => void, start?: 'here' | 'paste'): void {
  const who = first(c.name)
  const pinnedNow = placeInMapsLink(c.link)
  let found: Found | null = null
  let view: 'pick' | 'paste' | 'finding' | 'check' = 'pick'
  let busy = false
  // Each look for the phone's place has its number: one given up on (Not now, Try again) is never used when it answers late.
  let looking = 0
  const dialog = el('dialog', { class: 'booking-dialog parking-sheet spot-sheet', 'aria-labelledby': 'spot-sheet-title' }) as HTMLDialogElement
  const title = el('h2', { id: 'spot-sheet-title' }, `Pin ${who}'s exact spot`)
  const sub = el('p', { class: 'muted parking-sheet-sub' })
  const shut = el('button', { type: 'button', class: 'parking-sheet-close', 'aria-label': 'Close' }, '×')
  const body = el('div', { class: 'parking-sheet-body' })
  const error = el('p', { class: 'form-error', role: 'alert' })
  const foot = el('div', { class: 'parking-sheet-foot' })
  const close = (): void => { if (dialog.open) dialog.close() }
  shut.addEventListener('click', close)
  dialog.addEventListener('close', () => dialog.remove())
  const linkBox = el('input', { type: 'url', inputmode: 'url', class: 'spot-link-box', placeholder: 'maps.app.goo.gl/…', 'aria-label': 'Google Maps link', autocomplete: 'off' }) as HTMLInputElement

  const tile = (drawing: Drawing, words: string, small: string, act: () => void): HTMLElement => {
    const b = el('button', { type: 'button', class: 'wd-extra-tile spot-tile' },
      el('span', { class: 'wd-extra-top' }, el('span', { class: 'booking-extras-ring', 'aria-hidden': 'true' }, lineDrawing(drawing, 20))),
      el('span', { class: 'wd-extra-words' }, el('strong', {}, words), el('span', {}, small)))
    b.addEventListener('click', act)
    return b
  }
  const button = (words: string, cls: string, act: () => void): HTMLButtonElement => {
    const b = el('button', { type: 'button', class: cls }, words) as HTMLButtonElement
    b.addEventListener('click', act)
    return b
  }

  const findHere = (): void => {
    error.textContent = ''
    if (!('geolocation' in navigator)) { error.textContent = "This phone can't say where it is. Paste a link instead."; return }
    view = 'finding'; draw()
    const mine = ++looking
    const current = (): boolean => dialog.isConnected && mine === looking && view === 'finding'
    navigator.geolocation.getCurrentPosition((pos) => {
      if (!current()) return
      const metres = Math.round(pos.coords.accuracy)
      if (!(metres <= ROUGHEST_METRES)) {
        view = 'pick'; draw()
        error.textContent = `Your phone can only tell to about ${metres} metres, too rough for a door. Try again outside, or paste a link.`
        return
      }
      found = { spot: { lat: Math.round(pos.coords.latitude * 1e6) / 1e6, lng: Math.round(pos.coords.longitude * 1e6) / 1e6 }, metres }
      view = 'check'; draw()
    }, (err) => {
      if (!current()) return
      view = 'pick'; draw()
      error.textContent = err.code === err.PERMISSION_DENIED
        ? 'Your phone said no to sharing where it is. Allow location for Roundly in your phone\'s settings, or paste a link.'
        : "Your phone couldn't find where it is. Try again outside, or paste a link."
    }, { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 })
  }

  const useLink = async (): Promise<void> => {
    error.textContent = ''
    const typed = linkBox.value.trim()
    if (!typed) { linkBox.focus(); return }
    const link = /^https?:\/\//i.test(typed) ? typed.replace(/^http:/i, 'https:') : `https://${typed}`
    let spot = placeInMapsLink(link)
    if (!spot) {
      busy = true; draw()
      try {
        const { api } = await import('./api')
        const r = await api<{ lat: number | null; lng?: number }>(`/api/maps-place?u=${encodeURIComponent(link)}`)
        if (typeof r.lat === 'number' && typeof r.lng === 'number') spot = { lat: r.lat, lng: r.lng }
      } catch (e) {
        busy = false; draw()
        error.textContent = e instanceof Error && e.message ? e.message : "That link couldn't be read. Check you have signal and try again."
        return
      }
      busy = false
    }
    if (!spot) { draw(); error.textContent = "That link doesn't point at one door. In Google Maps, hold your finger on their door, tap Share, then Copy link."; return }
    found = { spot, metres: null }
    view = 'check'; draw()
  }

  // Saved onto the customer as they are now (read fresh, so a change on another phone is never written over), with Undo.
  const save = async (spot: Spot | null): Promise<void> => {
    if (busy) return
    // Someone not saved yet (New customer): the spot goes with them when they are.
    if (choose) { choose(spot ? spotLink(spot) : null); close(); return }
    busy = true; draw(); error.textContent = ''
    try {
      const { api, mutate } = await import('./api')
      const path = `/api/customers/${c.id}`
      const now = (await api<{ customer: { version: number; maps_link: string | null } }>(path)).customer
      const link = spot ? spotLink(spot) : null
      let key: string | null = null
      const ack = await mutate({ label: spot ? `Exact spot: ${c.name}` : `Exact spot off: ${c.name}`, method: 'PATCH', path, body: { version: now.version, maps_link: link }, onQueued: (m) => { key = m.key } })
      // No signal: it waits and goes by itself, like any change made without signal, with its Undo offered now.
      if (!ack.ok && ack.status !== 0) {
        busy = false; draw()
        // A refused change is not left parked behind the next try (GPT-6.1's review): the next Save reads them afresh.
        if (key) { const { dismiss } = await import('./api'); dismiss(key) }
        error.textContent = ack.status === 409 ? `${c.name}'s page changed on another phone. Tap Save this spot again.` : ack.error ?? "That didn't save."
        return
      }
      const { undoPatch } = await import('./undo')
      undoPatch({ text: `${spot ? `Exact spot saved for ${who}` : `Exact spot taken off for ${who}`}${ack.status === 0 ? '. It goes when there is signal.' : ''}`, label: `Undo exact spot: ${c.name}`, path, ack, version: now.version, body: { maps_link: now.maps_link ?? null }, after })
      close()
      after(link)
    } catch {
      busy = false; draw()
      error.textContent = 'No signal. Try again when there is signal.'
    }
  }

  const draw = (): void => {
    clear(body); clear(foot)
    if (view === 'pick' || view === 'paste') {
      sub.textContent = `Directions go here instead of the address, on every phone on your round. Best done at their door.`
      body.append(el('div', { class: 'wd-extra-tiles' },
        tile('focus', "I'm here now", 'Pin where your phone is', findHere),
        tile('reply', 'Paste a link', 'From Google Maps or a text', () => { view = 'paste'; error.textContent = ''; draw(); linkBox.focus() })))
      if (view === 'paste') {
        const use = button(busy ? 'Reading the link…' : 'Use this link', 'btn-secondary spot-use', () => void useLink())
        use.disabled = busy
        body.append(el('label', { class: 'spot-link' }, el('span', {}, 'Google Maps link'), linkBox), use,
          el('p', { class: 'muted spot-how' }, 'In Google Maps: hold your finger on their door, tap Share, then Copy link.'))
      }
      if (pinnedNow) {
        body.append(el('p', { class: 'spot-now' }, lineDrawing('pin', 16), 'Pinned now.', el('a', { href: spotLink(pinnedNow), target: '_blank', rel: 'noopener' }, 'See it on Google Maps')))
        const off = button('Take the pin off', 'btn-secondary', () => void save(null))
        off.disabled = busy
        foot.append(off)
      }
    } else if (view === 'finding') {
      sub.textContent = 'Finding where your phone is…'
      body.append(el('p', { class: 'spot-finding', 'aria-live': 'polite' }, lineDrawing('focus', 28), 'Finding where your phone is. Stand by their door.'))
      foot.append(button('Not now', 'btn-secondary', () => { looking++; view = 'pick'; draw() }))
    } else if (found) {
      sub.textContent = 'Check it, then save it.'
      body.append(
        el('div', { class: 'spot-found' }, lineDrawing('pin', 28),
          el('span', {}, el('strong', {}, found.metres !== null ? `Phone accuracy: about ${found.metres} metres` : 'From the link'),
            el('span', {}, found.metres !== null ? 'Where your phone is now.' : 'The spot the link points at.'))),
        el('a', { href: spotLink(found.spot), target: '_blank', rel: 'noopener', class: 'btn-secondary spot-look' }, 'Check it on Google Maps'))
      const keep = button(busy ? 'Saving…' : 'Save this spot', 'btn-primary parking-sheet-main', () => void save(found!.spot))
      keep.disabled = busy
      foot.append(keep, el('div', { class: 'spot-foot-two' },
        button('Try again', 'btn-secondary', () => { found = null; view = 'pick'; draw() }),
        button('Not now', 'btn-secondary', close)))
    }
    foot.hidden = !foot.childElementCount
  }
  dialog.append(el('div', { class: 'parking-sheet-head' }, el('div', { class: 'parking-sheet-titles' }, title, sub), shut), body, error, foot)
  document.body.append(dialog)
  if (start === 'paste') view = 'paste'
  draw()
  dialog.showModal()
  if (start === 'here') findHere()
  else if (start === 'paste') linkBox.focus()
}
